import { useEffect, useRef } from 'react';
import { subscriptionService } from '../../../marketplace/services/subscriptionService';
import { getSubscriptionFromResponse } from '../../../Redux/subscriptionSlice';

const POLL_INTERVAL_MS = 3000;
const ACTIVATION_TIMEOUT_MS = 60000;

/**
 * True only when we should stop polling and consider subscription activated (non-basic paid plan).
 */
function isActivated(sub) {
  return (
    sub != null &&
    sub.planType !== 'basic' &&
    sub.isActive === true
  );
}

/**
 * Polls GET /payments/subscription until subscription is activated (non-basic + isActive) or timeout.
 * Calls onActivated(sub) with the subscription object so caller can update Redux without an extra fetch.
 * Uses cache-busting; cleans up on unmount or when enabled becomes false.
 */
export function useSubscriptionPolling({ enabled, onActivated, onTimeout }) {
  const cancelledRef = useRef(false);
  const resolvedRef = useRef(false);
  const intervalIdRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const onActivatedRef = useRef(onActivated);
  const onTimeoutRef = useRef(onTimeout);

  onActivatedRef.current = onActivated;
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) return;

    cancelledRef.current = false;
    resolvedRef.current = false;

    const stopPolling = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };

    const resolve = (type, sub) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      cancelledRef.current = true;
      stopPolling();
      if (type === 'activated') {
        onActivatedRef.current?.(sub);
      } else {
        onTimeoutRef.current?.();
      }
    };

    const checkSubscription = async () => {
      if (cancelledRef.current || resolvedRef.current) return;
      try {
        const response = await subscriptionService.getSubscription({ cacheBust: true });
        if (cancelledRef.current || resolvedRef.current) return;
        const sub = getSubscriptionFromResponse(response);
        if (isActivated(sub)) {
          resolve('activated', sub);
          return;
        }
      } catch {
        if (cancelledRef.current || resolvedRef.current) return;
      }
    };

    intervalIdRef.current = setInterval(checkSubscription, POLL_INTERVAL_MS);
    checkSubscription();

    timeoutIdRef.current = setTimeout(() => {
      if (cancelledRef.current || resolvedRef.current) return;
      resolve('timeout');
    }, ACTIVATION_TIMEOUT_MS);

    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [enabled]);
}
